// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Shared HTML rendering utilities.
 *
 * This module is used by:
 * - server-side rendering (SSR) to render the initial HTML
 * - the client to lazily populate panes that SSR left empty
 */
import type {
  InitialState,
  SongbookBook,
  SongbookTag,
  SongbookAltVersionLink,
  SongbookAudioLink,
} from "./types";
import {
  getBookViewId,
  getEnabledBooks,
  joinUrl,
  resolveCoverUrl,
  resolveMultikeyJsonUrl,
  resolvePdfUrl,
  SONGBOOK_DATA_BASE_URL,
} from "./songbook";
import type { SongIndexEntry } from "./song-index";
import { displaySongTitle, normalizeForSearch } from "./song-index";
import type { Translator } from "@unilarva/translator";
import { renderRichTranslationHtml } from "./rich-translations";
import { htmlEncode } from "./generic/object-utils";
import type { GroupedSongResult } from "./song-results";
import { renderGroupedSongResultHtml } from "./song-result-renderer";
import { serializeViewUrl, writeSearchUrlState, type SearchUrlState } from "./view-url";

export type RenderOptions = {
  songIndexEntries?: readonly SongIndexEntry[];
  songId?: string | null;
  songGroup?: GroupedSongResult | null;
  songStatus?: "missing-id" | "not-found" | "unavailable" | "loading";
  searchState?: SearchUrlState;
  searchResults?: readonly GroupedSongResult[];
  searchTruncated?: boolean;
  searchUnavailable?: boolean;
};

function escAttr(s: string): string {
  return htmlEncode(s, true);
}

function escText(s: string): string {
  return htmlEncode(s, true);
}

function translatedText(t: Translator, key: string): string {
  return escText(t.translateKey(key));
}

function cls(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

function cssEscapeIdent(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]+/g, "-");
}

function renderIndexStatus(t: Translator, count: number | null): string {
  if (count == null) {
    return `<span data-i18n="index.loading">${escText(t.translateKey("index.loading"))}</span>`;
  }
  return `<span>${count}</span> <span data-i18n="index.results.status-label">${escText(t.translateKey("index.results.status-label"))}</span>`;
}

function richTranslationAttrs(t: Translator, key: string): string {
  return ` data-i18n-rich="${escAttr(key)}" data-i18n-rich-lang="${escAttr(t.getLanguage())}"`;
}

function bookDescriptionTitleAttrs(t: Translator, bookId: number): string {
  const key = `DYNAMIC.book.${bookId}.description`;
  const escVal = escAttr(t.translateKey(key));
  const escKey = escAttr(key);
  return ` data-i18n-title="${escKey}" title="${escVal}" data-i18n-aria-label="${escKey}" aria-label="${escVal}"`;
}

/**
 * Renders the inner HTML contents for a specific pane/view.
 *
 * Used by the client to lazily populate panes that were left empty by SSR.
 */
export function renderPaneContents(
  state: InitialState,
  translator: Translator,
  view: string,
  opts: RenderOptions = {},
): string {
  const normalizedView = view || "home";
  const enabled = getEnabledBooks(state.books);

  if (normalizedView === "home") {
    return renderHome(state, translator, enabled);
  }

  if (normalizedView === "search") {
    return renderSearch(state, translator, opts);
  }

  if (normalizedView === "song") return renderSong(state, translator, opts);

  if (normalizedView === "index") {
    return renderSongIndex(translator, opts.songIndexEntries ?? null);
  }

  for (let idx0 = 0; idx0 < enabled.length; idx0++) {
    const id = idx0 + 1;
    const book = enabled[idx0];
    const viewId = getBookViewId(book, id);
    if (viewId === normalizedView) {
      return renderBook(state, translator, book, id, viewId);
    }
  }

  return "";
}

/**
 * Renders the full application shell.
 *
 * Note: SSR includes only the initially requested view's content; other panes are
 * included as empty placeholders and populated on-demand by the client.
 */
export function renderApp(
  state: InitialState,
  translator: Translator,
  view: string,
  opts: RenderOptions = {},
): string {
  const enabled = getEnabledBooks(state.books);

  const currentBook = enabled
    .map((b, i) => ({ book: b, id: i + 1, viewId: getBookViewId(b, i + 1) }))
    .find(x => x.viewId === view);

  const isSearchView = view === "search";
  const isIndexView = view === "index";
  const songTitle =
    view === "song" && opts.songGroup
      ? displaySongTitle({ ...opts.songGroup.representative, ...opts.songGroup.commonMetadata })
      : "";

  const mainTitleKey = currentBook
    ? `DYNAMIC.book.${currentBook.id}.title`
    : isSearchView
      ? "search.title"
      : isIndexView
        ? "index.title"
        : view === "song"
          ? "song.title"
          : "site.title.short";

  const subTitleKey = currentBook
    ? `DYNAMIC.book.${currentBook.id}.subtitle`
    : isSearchView
      ? "site.title.short"
      : isIndexView
        ? "index.subtitle"
        : view === "song"
          ? "site.title.short"
          : "";

  const sideBookLinks = enabled
    .map((book, idx0) => {
      const id = idx0 + 1;
      const viewId = getBookViewId(book, id);
      const href = `/?view=${encodeURIComponent(viewId)}`;
      const isCur = viewId === view;
      const icons = renderTagIcons(state, translator, book, "text");
      return `
        <li>
          <a data-view-nav data-sb-view-target="${escAttr(viewId)}" href="${escAttr(href)}"${bookDescriptionTitleAttrs(translator, id)} ${isCur ? 'aria-current="page"' : ""}>
            <span class="sb-side-book-title-main" data-i18n="DYNAMIC.book.${id}.title">${translatedText(translator, `DYNAMIC.book.${id}.title`)}</span>
            <br />
            <span class="sb-side-book-title-sub" data-i18n="DYNAMIC.book.${id}.subtitle">${translatedText(translator, `DYNAMIC.book.${id}.subtitle`)}</span>
            ${icons}
          </a>
        </li>`;
    })
    .join("");

  const homeHref = `/`;

  const sidebarIconFile = (state.site.sidebarIconFile ?? "").trim();
  const sidebarIconUrl = sidebarIconFile
    ? `/sites/${encodeURIComponent(state.site.siteId)}/img/${encodeURIComponent(sidebarIconFile)}`
    : "";

  const curLang = translator.getLanguage();
  const currentLanguageInfo = translator.getLanguages().find(language => language.code === curLang);
  const currentLanguageLabel = currentLanguageInfo?.nativeName || curLang;

  const curTheme = (state.theme || "auto").trim();
  const curThemeKey =
    curTheme === "dark" ? "theme.dark" : curTheme === "light" ? "theme.light" : "theme.auto";
  const curThemeIcon = curTheme === "dark" ? "sbi-moon" : "sbi-sun";

  const themeDropdownItems = ["auto", "light", "dark"]
    .map(x => {
      const iconClass = x === "dark" ? "sbi-moon" : "sbi-sun";
      const labelKey = `theme.${x}`;
      const extraCls = x === "auto" ? " sb-theme-choice sb-theme-choice--auto" : " sb-theme-choice";

      return `<button
        type="button"
        class="sb-btn sb-btn--list sb-dropdown-item${extraCls}"
        data-sb-theme-choice="${escAttr(x)}"
        ${x === curTheme ? 'aria-current="true"' : ""}
        data-i18n-aria-label="${escAttr(labelKey)}"
        aria-label="${escAttr(translator.translateKey(labelKey))}"
        data-i18n-title="${escAttr(labelKey)}"
        title="${escAttr(translator.translateKey(labelKey))}"
      >
        <i class="${escAttr(iconClass)}" data-sb-theme-choice-icon="${escAttr(x)}" aria-hidden="true"></i>
        <span data-sb-theme-choice-text="${escAttr(x)}" data-i18n="${escAttr(labelKey)}">${translatedText(translator, labelKey)}</span>
      </button>`;
    })
    .join("");

  const sideHtml = `
    <aside class="sb-side" aria-hidden="true" inert>
      <div class="sb-side-header" data-sb-menu-toggle>
        <div class="sb-side-header-row">
          <div class="sb-side-brand">
            ${sidebarIconUrl ? `<img class="sb-side-icon" width=256 height=256 src="${escAttr(sidebarIconUrl)}" alt="${escAttr(translator.translateKey("site.title.short"))}" />` : ""}
            <div class="sb-side-brand-text">
              <div class="sb-side-brand-title" data-i18n="site.title.short">${translatedText(translator, "site.title.short")}</div>
              <div class="sb-side-brand-sub" data-i18n="site.navigation">${translatedText(translator, "site.navigation")}</div>
            </div>
          </div>

          <button
            type="button"
            class="sb-btn sb-btn--accent-surface sb-btn--icon sb-h-end"
            data-sb-menu-toggle
            data-i18n-aria-label="button.close"
            aria-label="${escAttr(translator.translateKey("button.close"))}"
            title="${escAttr(translator.translateKey("button.close"))}"
          ><i class="sbi-x" aria-hidden="true"></i></button>
        </div>
      </div>

      <div class="sb-side-scroll">
        <nav data-i18n-aria-label="site.navigation" aria-label="${escAttr(translator.translateKey("site.navigation"))}">
          <ul class="sb-side-nav">
            <li>
              <a data-view-nav data-sb-view-target="home" href="${escAttr(homeHref)}" ${view === "home" ? 'aria-current="page"' : ""}>
                <span class="sb-side-book-title-main" data-i18n="button.home">${translatedText(translator, "button.home")}</span>
                <span class="sb-side-tagicons sb-font-md"><i class="sbi-house" aria-hidden="true"></i></span>
              </a>
            </li>
            <li>
              <a data-view-nav data-sb-view-target="search" href="/?view=search" ${view === "search" ? 'aria-current="page"' : ""}>
                <span class="sb-side-book-title-main" data-i18n="search.title">${translatedText(translator, "search.title")}</span>
                <br />
                <span class="sb-side-book-title-sub" data-i18n="search.subtitle">${translatedText(translator, "search.subtitle")}</span>
                <!-- <span class="sb-side-tagicons"><i class="sbi-search" aria-hidden="true"></i></span> -->
              </a>
            </li>
            <li>
              <a data-view-nav data-sb-view-target="index" href="/?view=index" ${view === "index" ? 'aria-current="page"' : ""}>
                <span class="sb-side-book-title-main" data-i18n="index.title">${translatedText(translator, "index.title")}</span>
                <br />
                <span class="sb-side-book-title-sub" data-i18n="index.subtitle">${translatedText(translator, "index.subtitle")}</span>
                <!-- <span class="sb-side-tagicons"><i class="sbi-list-numbered" aria-hidden="true"></i></span> -->
              </a>
            </li>
            <li class="sb-side-nav-heading" aria-hidden="true">
              <span data-i18n="side.books-and-variants">${translatedText(translator, "side.books-and-variants")}</span>
            </li>
            ${sideBookLinks}
          </ul>
        </nav>
      </div>

      <div
        class="sb-side-footer"
        data-i18n-aria-label="site.preferences"
        aria-label="${escAttr(translator.translateKey("site.preferences"))}"
      >

        <details
          class="sb-dropdown sb-side-pref sb-side-theme"
          data-i18n-aria-label="theme.label"
          aria-label="${escAttr(translator.translateKey("theme.label"))}"
        >
          <summary
            class="sb-side-pref-summary sb-btn sb-btn--accent-surface"
            data-i18n-aria-label="theme.label"
            aria-label="${escAttr(translator.translateKey("theme.label"))}"
            data-i18n-title="theme.label"
            title="${escAttr(translator.translateKey("theme.label"))}"
          >
            <span><span data-i18n="theme.label">${translatedText(translator, "theme.label")}</span>:</span>
            <span class="sb-side-pref-selected" data-sb-theme-current>
              <i class="${escAttr(curThemeIcon)}" data-sb-theme-current-icon aria-hidden="true"></i>
              <span data-sb-theme-current-text data-i18n="${escAttr(curThemeKey)}">${translatedText(translator, curThemeKey)}</span>
            </span>
            <i class="sbi-caret-up" aria-hidden="true"></i>
          </summary>
          <div class="sb-dropdown-menu" role="menu">
            ${themeDropdownItems}
          </div>
        </details>

        <details class="sb-dropdown sb-side-pref sb-side-lang" aria-label="Language" data-sb-language-details>
          <summary class="sb-side-pref-summary sb-btn sb-btn--accent-surface" aria-label="Language" title="Language">
            <span>Language:</span>
            <span class="sb-side-pref-selected" data-i18n-language-current lang="${escAttr(curLang)}">${escText(currentLanguageLabel)}</span>
            <i class="sbi-caret-up" aria-hidden="true"></i>
          </summary>
          <div class="sb-dropdown-menu" role="group" data-i18n-language-options></div>
        </details>

      </div>
    </aside>`;

  // Render only the requested view's content; keep other panes empty.
  const homePaneContents = view === "home" ? renderHome(state, translator, enabled) : "";
  const homePanePopulatedAttr = homePaneContents ? ' data-sb-pane-populated="1"' : "";
  const homePane = `
    <section class="${cls("sb-pane", "sb-pane-home", view === "home" && "active")}" data-sb-pane="home"${homePanePopulatedAttr}>
      ${homePaneContents}
    </section>`;

  const searchPaneContents = view === "search" ? renderSearch(state, translator, opts) : "";
  const searchPanePopulatedAttr = searchPaneContents ? ' data-sb-pane-populated="1"' : "";
  const searchPane = `
    <section class="${cls("sb-pane", "sb-pane-search", view === "search" && "active")}" data-sb-pane="search"${searchPanePopulatedAttr}>
      ${searchPaneContents}
    </section>`;

  const indexPaneContents =
    view === "index" ? renderSongIndex(translator, opts.songIndexEntries ?? null) : "";
  const indexPanePopulatedAttr = indexPaneContents ? ' data-sb-pane-populated="1"' : "";
  const indexPane = `
    <section class="${cls("sb-pane", "sb-pane-index", view === "index" && "active")}" data-sb-pane="index"${indexPanePopulatedAttr}>
      ${indexPaneContents}
    </section>`;

  const bookPanes = enabled
    .map((book, idx0) => {
      const id = idx0 + 1;
      const viewId = getBookViewId(book, id);
      const isActive = viewId === view;
      const contents = isActive ? renderBook(state, translator, book, id, viewId) : "";
      const populatedAttr = contents ? ' data-sb-pane-populated="1"' : "";
      return `
        <article class="${cls("sb-pane", "sb-pane-pdf", isActive && "active")}" data-sb-pane="${escAttr(viewId)}"${populatedAttr}>
          ${contents}
        </article>`;
    })
    .join("");

  const songPaneContents = view === "song" ? renderSong(state, translator, opts) : "";
  const songPane = `
    <section class="${cls("sb-pane", "sb-pane-song", view === "song" && "active")}" data-sb-pane="song"${songPaneContents ? ' data-sb-pane-populated="1"' : ""}>
      ${songPaneContents}
    </section>`;
  const mainHtml = `${homePane}${searchPane}${indexPane}${songPane}${bookPanes}`;

  // Modals are rendered once globally so that deep-linking directly to a book
  // view still has the modal DOM available.
  const modalsHtml = renderAllModals(state, translator, enabled);

  const scrollTopLabel = translator.translateKey("button.scroll-top");

  return `
    <div class="sb-shell">
      <header class="sb-topbar">
        <div class="sb-topbar-start">
          <button
            type="button"
            class="sb-btn sb-btn--accent-surface sb-btn--icon sb-menu-btn"
            data-sb-menu-toggle
            data-i18n-aria-label="site.navigation"
            aria-label="${escAttr(translator.translateKey("site.navigation"))}"
            data-i18n-title="site.navigation"
            title="${escAttr(translator.translateKey("site.navigation"))}"
            aria-expanded="false"
          ><i class="sbi-hamburger" aria-hidden="true"></i></button>
          <!-- <a class="sb-menu-btn" data-view-nav data-sb-view-target="home" href="${escAttr(homeHref)}"><i class="sbi-house" aria-hidden="true"></i></a> -->
        </div>
        <div class="sb-title">
          <h1 id="sb-view-title" class="sb-title-main" tabindex="-1"${songTitle ? "" : ` data-i18n="${escAttr(mainTitleKey)}"`}>${songTitle ? escText(songTitle) : translatedText(translator, mainTitleKey)}</h1>
          <span class="sb-title-sub" role="doc-subtitle"${subTitleKey ? ` data-i18n="${escAttr(subTitleKey)}"` : ""}>${subTitleKey ? translatedText(translator, subTitleKey) : ""}</span>
        </div>
        <div class="sb-topbar-end"></div>
      </header>

      <div class="sb-body">
        ${sideHtml}
        <div class="sb-side-backdrop" data-sb-side-backdrop aria-hidden="true"></div>
        <main class="sb-main" aria-labelledby="sb-view-title">
          ${mainHtml}
        </main>
        <button
          type="button"
          class="sb-btn sb-btn--accent-surface sb-btn--icon"
          data-sb-scroll-top-global
          data-i18n-title="button.scroll-top"
          data-i18n-aria-label="button.scroll-top"
          aria-label="${escAttr(scrollTopLabel)}"
          title="${escAttr(scrollTopLabel)}"
        >
          <i class="sbi-arrow-up-circle" aria-hidden="true"></i>
        </button>
      </div>
    </div>
    ${modalsHtml}
  `;
}

function renderHome(state: InitialState, t: Translator, enabled: SongbookBook[]): string {
  const coverWidth =
    state.site.bookCoverImageWidth && state.site.bookCoverImageWidth > 0
      ? state.site.bookCoverImageWidth
      : 1;
  const coverHeight =
    state.site.bookCoverImageHeight && state.site.bookCoverImageHeight > 0
      ? state.site.bookCoverImageHeight
      : 1;

  const cards = enabled
    .map((book, idx0) => {
      const id = idx0 + 1;
      const viewId = getBookViewId(book, id);
      const viewHref = `/?view=${encodeURIComponent(viewId)}`;
      const pdfUrl = resolvePdfUrl(state.books, book);
      const coverUrl = resolveCoverUrl(state.books, book);

      const tagIconsCover = renderTagIcons(state, t, book, "cover");
      const backInner = renderCardBackInner(state, t, book, id).trim();
      const cardFooter = renderCardFooter(t, id, viewId, viewHref, pdfUrl);

      return `
        <article class="sb-card sb-card--flippable">
          <div class="sb-card-flip">

            <!-- Card: Front -->
            <section class="sb-card-face sb-card-front">
              <span class="sb-card-flip-corner" aria-hidden="true"></span>
              <button type="button" class="sb-card-flip-btn" data-i18n-aria-label="card.flip.show-back" data-i18n-title="card.flip.show-back" aria-label="${escAttr(t.translateKey("card.flip.show-back"))}" title="${escAttr(t.translateKey("card.flip.show-back"))}"><i class="sbi-arrow-clockwise" aria-hidden="true"></i></button>
              <a class="sb-card-cover sb-spinner" data-view-nav data-sb-view-target="${escAttr(viewId)}" href="${escAttr(viewHref)}"${bookDescriptionTitleAttrs(t, id)}>
                ${tagIconsCover}
                <img src="${escAttr(coverUrl)}" alt="${escAttr(t.translateKey("cover.image.text"))}"${idx0 === 0 ? ' fetchpriority="high"' : idx0 >= 2 ? ' fetchpriority="conservative"' : ""} width="${coverWidth}" height="${coverHeight}" />
              </a>
              ${cardFooter}
            </section>

            <!-- Card: Back -->
            <section class="sb-card-face sb-card-back" inert>
              <span class="sb-card-flip-corner" aria-hidden="true"></span>
              <button type="button" class="sb-card-flip-btn" data-i18n-aria-label="card.flip.show-front" data-i18n-title="card.flip.show-front" aria-label="${escAttr(t.translateKey("card.flip.show-front"))}" title="${escAttr(t.translateKey("card.flip.show-front"))}"><i class="sbi-arrow-clockwise" aria-hidden="true"></i></button>
              <div class="sb-card-back-inner">${backInner}</div>
              ${cardFooter}
            </section>

          </div>
        </article>`;
    })
    .join("");

  const feeler = renderRichTranslationHtml(t, "homepage.feeler", state.site);
  const text = renderRichTranslationHtml(t, "homepage.text", state.site);
  const footer = renderRichTranslationHtml(t, "homepage.footer", state.site);

  const allFilesUrlRaw = (state.site.allFilesUrl ?? "").trim();
  const allFilesLinkHtml = allFilesUrlRaw
    ? `<a class="sb-allfiles-link" href="${escAttr(allFilesUrlRaw)}" target="_blank" rel="nofollow" data-i18n="link.allfiles">${escText(t.translateKey("link.allfiles"))}</a>`
    : "";

  // Note: We include the number of cards as a data-attribute so CSS can
  // optionally cap the grid column max-width for small counts (1–4).
  // See `apps/client/src/styles.css` ("Home card-grid sizing").
  const cardCountAttr = ` data-sb-card-count="${enabled.length}"`;

  return `
    <section class="sb-home-search-cta">
      <a
        class="sb-btn sb-btn--surface sb-home-search-btn"
        data-view-nav
        data-sb-view-target="search"
        href="/?view=search"
      >
        <i class="sbi-search" aria-hidden="true"></i>
        <span data-i18n="button.search">${translatedText(t, "button.search")}</span>
      </a>
    </section>
    <section class="sb-cards"${cardCountAttr}>${cards}</section>
    <section class="sb-home-text">
      <div class="sb-home-text-feeler"${richTranslationAttrs(t, "homepage.feeler")}>${feeler}</div>
      <div${richTranslationAttrs(t, "homepage.text")}>${text}</div>
    </section>
    <section class="sb-home-footer sb-muted">
      ${allFilesLinkHtml}
      <div${richTranslationAttrs(t, "homepage.footer")}>${footer}</div>
    </section>
  `;
}

/** Renders a consolidated song or translated navigation/error state. */
export function renderSong(state: InitialState, t: Translator, opts: RenderOptions = {}): string {
  const songId = opts.songId ?? state.songId ?? "";
  const status = opts.songGroup
    ? "ready"
    : (opts.songStatus ?? (songId ? "unavailable" : "missing-id"));
  const body = opts.songGroup
    ? renderGroupedSongResultHtml(opts.songGroup, {
        translator: t,
        books: state.books,
        destinationHashParam: state.site.pdfDestinationHashParam,
        defaultLanguage: state.site.defaultLanguage,
        songPage: true,
        showCovers: new Set(getEnabledBooks(state.books).map(book => book.source_book_id)).size > 1,
      })
    : status === "loading"
      ? `<div class="sb-search-status sb-search-status--visible" role="status">
           <div class="sb-search-status-inner">
             <span class="sb-search-status-text" data-i18n="song.loading">${translatedText(t, "song.loading")}</span>
             <span class="sb-search-status-spinner" aria-hidden="true"></span>
           </div>
         </div>`
      : `<p class="sb-muted" data-i18n="song.${status}">${translatedText(t, `song.${status}`)}</p>
       <p>${["index", "search"]
         .map(view => {
           const key = view === "index" ? "song.list-all" : "song.search";
           const href = serializeViewUrl(
             { view, lang: t.getLanguage() },
             { defaultLanguage: state.site.defaultLanguage },
           );
           return `<a class="sb-btn sb-btn--surface" data-view-nav data-sb-view-target="${view}" href="${escAttr(href)}" data-i18n="${key}">${translatedText(t, key)}</a>`;
         })
         .join("\n          ")}</p>`;
  return `
    <section class="sb-search" data-sb-song-root
      data-sb-song-id="${escAttr(songId)}" data-sb-song-status="${status}"${status !== "unavailable" && status !== "loading" ? ' data-sb-song-loaded="1"' : ""}>
      ${body}
    </section>`;
}

function renderSearch(state: InitialState, t: Translator, opts: RenderOptions = {}): string {
  const searchState = opts.searchState ?? state.searchState;
  const selected = (values: string[] | "all" | null | undefined, value: string): boolean =>
    !values || values === "all" || values.includes(value);
  const allSelected = (values: string[] | "all" | null | undefined, known: string[]): boolean =>
    known.every(value => selected(values, value));

  const enabledBooks = getEnabledBooks(state.books);

  // Precompute known variants and sources so SSR can render toggles on first paint.
  const knownVariants = Array.from(
    new Set<string>(
      enabledBooks.map(b => String((b as any).variant || "default").trim()).filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const knownSources = Array.from(
    new Set<string>(
      enabledBooks.map(b => String((b as any).source_book_id || "").trim()).filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const hideVariants = knownVariants.length <= 1;
  const hideSources = knownSources.length <= 1;

  const renderVariantToggles = (variants: string[]): string => {
    if (variants.length <= 1) return "";

    const allId = "sb-search-variant-all";
    const allOn = allSelected(searchState?.variants, variants);

    const variantTagIcons = (variant: string): string => {
      const tagList = state.books.tags ?? [];
      const variantDefTags = (state.books as any).variant_types?.[variant]?.tag_ids as
        string[] | undefined;
      if (!variantDefTags || variantDefTags.length === 0) return "";

      const icons: string[] = [];
      for (const tagId of variantDefTags) {
        const tag = tagList.find(t => t?.id === tagId);
        if (!tag) continue;
        const iconKey = `DYNAMIC.tag.${tag.id}.iconfile`;
        const textKey = `DYNAMIC.tag.${tag.id}.text`;
        const iconUrl = t.translateKey(iconKey);
        if (!iconUrl) continue;
        icons.push(
          `<img class="sb-tag-icon sb-tag-icon--inline" src="${escAttr(iconUrl)}" data-i18n-alt="${escAttr(textKey)}" alt="${escAttr(t.translateKey(textKey))}" fetchpriority="high" width="24" height="24" />`,
        );
      }
      if (icons.length === 0) return "";
      return `<span class="sb-search-variant-icons">${icons.join("")}</span>`;
    };

    const parts: string[] = [];
    parts.push(`
      <input id="${escAttr(allId)}" type="checkbox" ${allOn ? "checked" : ""} data-sb-search-variant="all" disabled />
      <label class="sb-btn sb-btn--surface sb-search-toggle-all" for="${escAttr(allId)}" data-i18n="search.variants.all">${translatedText(t, "search.variants.all")}</label>
    `);

    // Ensure "default" is first.
    const ordered = [...variants];
    const defaultIdx = ordered.findIndex(v => v.trim().toLowerCase() === "default");
    if (defaultIdx > 0) {
      const [d] = ordered.splice(defaultIdx, 1);
      ordered.unshift(d);
    }

    ordered.forEach(v => {
      const id = `sb-search-variant-${cssEscapeIdent(v)}`;
      const iconsHtml = variantTagIcons(v);
      const dynamicLabelKey = `DYNAMIC.variant.${v}.label`;
      const dynamicLabel = t.translateKey(dynamicLabelKey);
      const knownLabelKey = (() => {
        const key = v.trim().toLowerCase();
        return key === "default" || key === "lyrics" || key === "bassclef" || key === "charango"
          ? `search.variant.${key}`
          : null;
      })();
      const labelText = dynamicLabel || (knownLabelKey ? t.translateKey(knownLabelKey) : v);
      parts.push(`
        <input id="${escAttr(id)}" type="checkbox" ${selected(searchState?.variants, v) ? "checked" : ""} data-sb-search-variant="${escAttr(v)}" disabled${allOn ? ' data-sb-hidden="1"' : ""} />
        <label class="sb-btn sb-btn--surface sb-search-variant" for="${escAttr(id)}"${allOn ? ' data-sb-hidden="1"' : ""}>
          <span class="sb-search-variant-label"${dynamicLabel ? "" : knownLabelKey ? ` data-i18n="${escAttr(knownLabelKey)}"` : ""}>${escText(labelText)}</span>
          ${iconsHtml}
        </label>
      `);
    });

    return parts.join("");
  };

  const renderSourceToggles = (sources: string[]): string => {
    if (sources.length <= 1) return "";

    const allId = "sb-search-source-all";
    const allOn = allSelected(searchState?.books, sources);

    const parts: string[] = [];
    parts.push(`
      <input id="${escAttr(allId)}" type="checkbox" ${allOn ? "checked" : ""} data-sb-search-source="all" disabled />
      <label class="sb-btn sb-btn--surface sb-search-toggle-all" for="${escAttr(allId)}" data-i18n="search.sources.all">${translatedText(t, "search.sources.all")}</label>
    `);

    const sourceImage = (sourceId: string): string | null => {
      const match = enabledBooks.find(b => (b as any).source_book_id === sourceId);
      if (!match) return null;
      return resolveCoverUrl(state.books, match);
    };

    sources.forEach(s => {
      const id = `sb-search-source-${cssEscapeIdent(s)}`;
      const cover = sourceImage(s);
      const coverHtml = cover
        ? `<img class="sb-search-source-cover" src="${escAttr(cover)}" alt="Book cover image" aria-hidden="true" width="96" height="96" />`
        : "";
      const title = t.translateKey(`DYNAMIC.source.${s}.title`) || s;
      parts.push(`
        <input id="${escAttr(id)}" type="checkbox" ${selected(searchState?.books, s) ? "checked" : ""} data-sb-search-source="${escAttr(s)}" disabled${allOn ? ' data-sb-hidden="1"' : ""} />
        <label class="sb-btn sb-btn--surface sb-search-source" for="${escAttr(id)}"${allOn ? ' data-sb-hidden="1"' : ""}>
          ${coverHtml}
          <span class="sb-search-source-label">${escText(title)}</span>
        </label>
      `);
    });

    return parts.join("");
  };

  const entityDefinitions = [
    { id: "all", key: "search.entity.all" },
    { id: "titles", key: "search.entity.titles" },
    { id: "lyrics", key: "search.entity.lyrics" },
    { id: "translations", key: "search.entity.translations" },
    { id: "chapters", key: "search.entity.chapters" },
    { id: "authors", key: "search.entity.authors" },
    { id: "offered_to", key: "search.entity.offered_to" },
    { id: "extra_info", key: "search.entity.extra_info" },
    { id: "tags", key: "search.entity.tags" },
    { id: "numbers", key: "search.entity.numbers" },
    { id: "ids", key: "search.entity.ids" },
  ];
  const allEntities = allSelected(
    searchState?.entities,
    entityDefinitions.slice(1).map(x => x.id),
  );
  const entityToggles = entityDefinitions
    .map(x => {
      const inputId = `sb-search-entity-${x.id}`;
      const extraCls = x.id === "all" ? " sb-search-toggle-all" : "";
      const extraAttrs = x.id === "all" || !allEntities ? "" : ' data-sb-hidden="1"';
      const checked = x.id === "all" ? allEntities : selected(searchState?.entities, x.id);
      return `
        <input id="${escAttr(inputId)}" type="checkbox" ${checked ? "checked" : ""} data-sb-search-entity="${escAttr(x.id)}" disabled${extraAttrs} />
        <label class="sb-btn sb-btn--surface${extraCls}" for="${escAttr(inputId)}"${extraAttrs} data-i18n="${escAttr(x.key)}">${translatedText(t, x.key)}</label>
      `;
    })
    .join("");

  const results = opts.searchResults;
  const ready = results !== undefined;
  const appearances = results?.reduce((count, group) => count + group.appearances.length, 0) ?? 0;
  const statusKey = opts.searchUnavailable
    ? "song.unavailable"
    : !ready
      ? "search.loading"
      : !searchState?.query.trim()
        ? "search.idle"
        : results.length === 0
          ? "search.results.none"
          : `search.results.status.${opts.searchTruncated ? "truncated" : "full"}${results.length === 1 ? (appearances === 1 ? ".single" : ".one") : ""}`;
  const statusText = t.translateKey(statusKey, {
    values: { count: results?.length ?? 0, appearances },
  });
  const filterParams = new URLSearchParams();
  if (searchState) writeSearchUrlState(filterParams, searchState);
  const hiddenFilters = (["books", "variants", "entities"] as const)
    .map(key => {
      const value = filterParams.get(key);
      return value !== null
        ? `<input type="hidden" name="${key}" value="${escAttr(value)}" />`
        : "";
    })
    .join("");
  return `
    <section class="sb-search" data-sb-search-root${ready ? ` data-sb-search-ssr="1" data-sb-search-truncated="${opts.searchTruncated ? "1" : "0"}"` : ""}>
      <p class="sb-muted" data-i18n="search.description">${translatedText(t, "search.description")}</p>
      <form method="get" action="/" data-sb-search-form>
      <input type="hidden" name="view" value="search" />

      <div class="sb-search-controls">
        <label class="sb-search-label">
          <span data-i18n="search.input.label">${translatedText(t, "search.input.label")}</span>
          <div class="sb-search-input-row">
            <i class="sbi-search" aria-hidden="true"></i>
            <input
              type="search"
              name="q"
              value="${escAttr(searchState?.query ?? "")}"
              class="sb-search-input"
              data-sb-search-input
              data-i18n-placeholder="search.input.placeholder"
              placeholder="${escAttr(t.translateKey("search.input.placeholder"))}"
              autocomplete="off"
              spellcheck="false"
            />
            <button
              type="button"
              class="sb-btn sb-btn--surface sb-btn--icon sb-search-clear"
              data-sb-search-clear
              data-i18n-aria-label="button.clear"
              aria-label="${escAttr(t.translateKey("button.clear"))}"
              title="${escAttr(t.translateKey("button.clear"))}"
            ><i class="sbi-x" aria-hidden="true"></i></button>
          </div>
        </label>
      </div>

      ${hiddenFilters}
      <input type="hidden" name="lang" value="${escAttr(t.getLanguage())}" />
      <noscript><button type="submit" class="sb-btn sb-btn--surface" data-i18n="button.search">${translatedText(t, "button.search")}</button></noscript>
      </form>

      <div class="sb-search-filters-head">
        <div
          class="sb-search-filters-summary sb-muted"
          data-sb-search-filters-summary
          data-i18n="search.filters.selected-summary"
        >
          <!-- skip on initial render -->
        </div>
        <button
          type="button"
          class="sb-btn sb-btn--surface"
          data-sb-search-filters-toggle
          data-i18n-title="search.filters.toggle"
          data-i18n-aria-label="search.filters.toggle"
          title="${escAttr(t.translateKey("search.filters.toggle"))}"
          aria-label="${escAttr(t.translateKey("search.filters.toggle"))}"
          aria-expanded="true"
        >
          <i class="sbi-caret-down" aria-hidden="true"></i>
          <span data-i18n="search.filters.toggle">${translatedText(t, "search.filters.toggle")}</span>
        </button>
      </div>

      <div class="sb-search-filters" data-sb-search-filters data-open="1">
        <section class="sb-search-filter" data-sb-search-filter-sources${hideSources ? ' data-sb-hidden="1" style="display:none;"' : ""}>
          <h2 class="sb-search-filter-title"><span data-i18n="search.filters.sources">${translatedText(t, "search.filters.sources")}</span> <span class="sb-search-filter-count sb-muted sb-fw-lighter" data-sb-search-count-sources></span></h2>
          <div class="sb-toggle-group sb-search-toggle-group" data-sb-search-sources>
            ${hideSources ? "" : renderSourceToggles(knownSources)}
          </div>
        </section>
        <section class="sb-search-filter" data-sb-search-filter-variants${hideVariants ? ' data-sb-hidden="1" style="display:none;"' : ""}>
          <h2 class="sb-search-filter-title"><span data-i18n="search.filters.variants">${translatedText(t, "search.filters.variants")}</span> <span class="sb-search-filter-count sb-muted sb-fw-lighter" data-sb-search-count-variants></span></h2>
          <div class="sb-toggle-group sb-search-toggle-group" data-sb-search-variants>
            ${hideVariants ? "" : renderVariantToggles(knownVariants)}
          </div>
        </section>
        <section class="sb-search-filter" data-sb-search-filter-entities>
          <h2 class="sb-search-filter-title"><span data-i18n="search.filters.entities">${translatedText(t, "search.filters.entities")}</span> <span class="sb-search-filter-count sb-muted sb-fw-lighter" data-sb-search-count-entities></span></h2>
          <div class="sb-toggle-group sb-search-toggle-group" data-sb-search-entities>
            ${entityToggles}
          </div>
        </section>
      </div>

      <div class="sb-search-status sb-search-status--error" data-sb-search-error hidden>
        <div class="sb-search-status-inner">
          <span class="sb-search-status-text" data-sb-search-error-text></span>
        </div>
      </div>

      <div class="sb-search-status${ready || opts.searchUnavailable ? " sb-search-status--visible sb-search-status--no-spinner" : ""}" data-sb-search-status>
        <div class="sb-search-status-inner">
          <span
            class="sb-search-status-text"
            data-sb-search-status-text
          >${escText(statusText)}</span>
          <span class="sb-search-status-spinner" aria-hidden="true"></span>
        </div>
      </div>

      <section class="sb-search-results" data-sb-search-results${results?.length ? ' data-sb-has-results="1"' : ""}>
        ${
          results?.length
            ? `<div class="sb-search-hitlist">
          ${results.map(group => renderGroupedSongResultHtml(group, { translator: t, books: state.books, destinationHashParam: state.site.pdfDestinationHashParam, queryNorm: normalizeForSearch(searchState?.query ?? ""), showCovers: knownSources.length > 1, defaultLanguage: state.site.defaultLanguage })).join("\n")}
        </div>`
            : ""
        }
      </section>
    </section>
  `;
}

function sortIndexEntries(
  entries: readonly SongIndexEntry[],
  mode: "title" | "author",
): SongIndexEntry[] {
  return [...entries].sort((a, b) => {
    const aAuthor = (a.author ?? "").trim();
    const bAuthor = (b.author ?? "").trim();
    if (mode === "author") {
      if (!aAuthor && bAuthor) return 1;
      if (aAuthor && !bAuthor) return -1;
      const byAuthor = aAuthor.localeCompare(bAuthor, undefined, { sensitivity: "base" });
      if (byAuthor !== 0) return byAuthor;
    }
    const byTitle = a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
    if (byTitle !== 0) return byTitle;
    return a.songId.localeCompare(b.songId, undefined, { sensitivity: "base" });
  });
}

export function renderSongIndexList(
  t: Translator,
  entries: readonly SongIndexEntry[],
  mode: "title" | "author",
): string {
  const ordered = sortIndexEntries(entries, mode);
  if (ordered.length === 0) {
    return `<p class="sb-muted" data-i18n="index.empty">${escText(t.translateKey("index.empty"))}</p>`;
  }

  const items = ordered
    .map((entry, idx0) => {
      const title = entry.title.trim();
      const author = (entry.author ?? "").trim();
      const href = serializeViewUrl({ view: "song", songId: entry.songId, lang: t.getLanguage() });
      const n = idx0 + 1;
      const authorLine = author
        ? `<span class="sb-index-subline sb-muted"><span data-i18n="index.by">${escText(t.translateKey("index.by"))}</span>: <span>${escText(author)}</span></span>`
        : `<span class="sb-index-subline sb-muted" data-i18n="index.unknown-author">${escText(t.translateKey("index.unknown-author"))}</span>`;
      const titleLine = `<span class="sb-index-subline">${escText(title)}</span>`;
      const text =
        mode === "author"
          ? `<span class="sb-index-mainline">${author ? escText(author) : `<span data-i18n="index.unknown-author">${escText(t.translateKey("index.unknown-author"))}</span>`}</span>${titleLine}`
          : `<span class="sb-index-mainline">${escText(title)}</span>${authorLine}`;
      return `
      <li class="sb-index-item" data-sb-index-author="${escAttr(author)}" data-sb-index-title="${escAttr(title)}">
        <a id="${escText(entry.songId)}" class="sb-index-link" data-view-nav data-sb-view-target="song" href="${escAttr(href)}">
          ${n % 10 === 0 ? '<span class="sb-index-number sb-muted">(' + n + ")</span>" : "<span></span>"}
          <span class="sb-index-text">${text}</span>
        </a>
      </li>`;
    })
    .join("");

  return `<ol class="sb-index-list" data-sb-index-mode="${escAttr(mode)}">${items}</ol>`;
}

function renderSongIndex(t: Translator, entries: readonly SongIndexEntry[] | null): string {
  const titleId = "sb-index-sort-title";
  const authorId = "sb-index-sort-author";
  const count = entries?.length ?? 0;
  const body = entries ? renderSongIndexList(t, entries, "title") : "";
  const statusText = renderIndexStatus(t, entries ? count : null);

  return `
    <section class="sb-index" data-sb-index-root${entries ? ' data-sb-index-loaded="1"' : ""}>
      <div class="sb-index-controls sb-toggle-group" role="group" data-i18n-aria-label="index.sort.label" aria-label="${escAttr(t.translateKey("index.sort.label"))}">
        <input id="${titleId}" type="radio" name="sb-index-sort" value="title" data-sb-index-sort="title" checked />
        <label class="sb-btn sb-btn--surface" for="${titleId}" data-i18n="index.sort.titles">${translatedText(t, "index.sort.titles")}</label>
        <input id="${authorId}" type="radio" name="sb-index-sort" value="author" data-sb-index-sort="author" />
        <label class="sb-btn sb-btn--surface" for="${authorId}" data-i18n="index.sort.authors">${translatedText(t, "index.sort.authors")}</label>
      </div>
      <div class="sb-search-status sb-search-status--error" data-sb-index-error hidden>
        <div class="sb-search-status-inner">
          <span class="sb-search-status-text" data-sb-index-error-text></span>
        </div>
      </div>
      <div class="sb-search-status sb-index-status sb-search-status--visible ${entries ? "sb-search-status--no-spinner" : ""}" data-sb-index-status>
        <div class="sb-search-status-inner">
          <span class="sb-search-status-text" data-sb-index-status-text>${statusText}</span>
          <span class="sb-search-status-spinner" aria-hidden="true"></span>
        </div>
      </div>
      <div class="sb-index-card sb-search-hit" data-sb-index-card${entries ? ' data-sb-has-results="1"' : ""}>
        <div class="sb-index-results" data-sb-index-results>${body}</div>
      </div>
      ${entries ? "" : `<noscript><p class="sb-muted" data-i18n="index.noscript">${translatedText(t, "index.noscript")}</p></noscript>`}
    </section>`;
}

function renderCardFooter(
  t: Translator,
  id: number,
  viewId: string,
  viewHref: string,
  pdfUrl: string,
): string {
  const escViewKey = "button.view";
  const escViewVal = escText(t.translateKey(escViewKey));
  const escDownloadKey = "button.download";
  const escDownloadVal = escText(t.translateKey(escDownloadKey));
  const bookTitleKey = `DYNAMIC.book.${id}.title`;
  const downloadLabel = escAttr(
    t.translateKey("button.download-book", {
      values: { bookTitle: t.translateKey(bookTitleKey) },
    }),
  );
  return `
              <div class="sb-card-footer">
                <a class="sb-card-titles" data-view-nav data-sb-view-target="${escAttr(viewId)}" href="${escAttr(viewHref)}"${bookDescriptionTitleAttrs(t, id)}>
                  <h2 class="sb-card-title" data-i18n="DYNAMIC.book.${id}.title">${translatedText(t, `DYNAMIC.book.${id}.title`)}</h2>
                  <span class="sb-card-subtitle" data-i18n="DYNAMIC.book.${id}.subtitle">${translatedText(t, `DYNAMIC.book.${id}.subtitle`)}</span>
                </a>
                <div class="sb-card-actions">
                  <a class="sb-btn sb-btn--surface" data-view-nav data-sb-view-target="${escAttr(viewId)}" href="${escAttr(viewHref)}"${bookDescriptionTitleAttrs(t, id)}>
                    <i class="sbi-eye" aria-hidden="true"></i> <span data-i18n="${escViewKey}">${escViewVal}</span>
                  </a>
                  <a class="sb-btn sb-btn--surface sb-muted" href="${escAttr(pdfUrl)}" data-i18n-title="${escDownloadKey}" title="${escDownloadVal}" data-sb-download-book-title="${escAttr(bookTitleKey)}" data-i18n-aria-label="button.download-book" aria-label="${downloadLabel}" download>
                    <i class="sbi-download" aria-hidden="true"></i> <span data-i18n="${escDownloadKey}">${escDownloadVal}</span>
                  </a>
                </div>
              </div>`;
}

function renderCardBackInner(
  state: InitialState,
  t: Translator,
  book: SongbookBook,
  bookId: number,
): string {
  const parts: string[] = [];

  if (book.altview_url) {
    parts.push(`
      <div class="sb-card-section">
        <a class="sb-card-link" href="${escAttr(book.altview_url)}" target="_blank" rel="nofollow">
          <span data-i18n="card.link.altview">${translatedText(t, "card.link.altview")}</span>
        </a>
      </div>
    `);
  }

  const audio = renderAudioLinks(state, t, book, bookId);
  if (audio) parts.push(audio);

  const printouts = renderPrintoutLinks(state, t, book, bookId);
  if (printouts) parts.push(printouts);

  if (parts.length == 0) {
    return `
      <div class="sb-card-section">
        <span data-i18n="no-info">${translatedText(t, "no-info")}</span>
      </div>
    `;
  }

  return parts.join("");
}

function renderAudioLinks(
  state: InitialState,
  t: Translator,
  book: SongbookBook,
  bookId: number,
): string {
  const enabled = (book.audio_links ?? []).filter(
    (x): x is SongbookAudioLink => !!x && !!x.enabled,
  );
  if (enabled.length === 0) return "";

  const items = enabled
    .map((link, idx0) => {
      const n = idx0 + 1;
      const key = `DYNAMIC.book.${bookId}.audio.title.${n}`;
      return `<li><a class="sb-card-link" href="${escAttr(link.url)}" target="_blank" rel="nofollow"><span data-i18n="${escAttr(key)}">${translatedText(t, key)}</span></a></li>`;
    })
    .join("");

  return `
    <div class="sb-card-section">
      <h3 data-i18n="card.heading.audio">${translatedText(t, "card.heading.audio")}</h3>
      <ul class="sb-card-list">
        ${items}
      </ul>
    </div>
  `;
}

function renderPrintoutLinks(
  state: InitialState,
  t: Translator,
  book: SongbookBook,
  bookId: number,
): string {
  const ext = "." + book.file.split(".").pop();
  const basename = book.file.replace(/\.[^/.]+$/, "");
  const bookBaseDir = joinUrl(SONGBOOK_DATA_BASE_URL, state.books.book_path);
  const printoutsDir = joinUrl(bookBaseDir, "printouts");

  const items: string[] = [];

  if (book.has_printout_easy_file) {
    const url = joinUrl(printoutsDir, `printout-EASY_${basename}-on-A4-sidebyside-simple${ext}`);
    items.push(
      renderPrintoutListItem(t, {
        href: url,
        labelKey: "card.printout.easy.title",
        infoModalId: "sb-modal-printout-easy",
      }),
    );
  }

  if (book.has_printout_booklet_file) {
    const url = joinUrl(
      printoutsDir,
      `printout-BOOKLET_${basename}-on-A4-doublesided-needs-cutting${ext}`,
    );
    items.push(
      renderPrintoutListItem(t, {
        href: url,
        labelKey: "card.printout.booklet.title",
        infoModalId: "sb-modal-printout-booklet",
      }),
    );
  }

  const extra = (book.extra_printout_links ?? []).filter(
    (x): x is SongbookAltVersionLink => !!x && !!x.enabled,
  );
  extra.forEach((link, idx0) => {
    const n = idx0 + 1;
    const key = `DYNAMIC.book.${bookId}.extraprintout.title.${n}`;
    const infoModalId = `sb-modal-book-${bookId}-extraprintout-${n}`;
    const href = resolveExtraPrintoutUrl(state, link.file);
    items.push(
      renderPrintoutListItem(t, {
        href,
        labelKey: key,
        infoModalId,
      }),
    );
  });

  if (items.length === 0) return "";

  return `
    <div class="sb-card-section">
      <h3 data-i18n="card.heading.printouts">${translatedText(t, "card.heading.printouts")}</h3>
      <ul class="sb-card-list">
        ${items.join("")}
      </ul>
    </div>
  `;
}

function renderPrintoutListItem(
  t: Translator,
  opts: { href: string; labelKey: string; infoModalId: string },
): string {
  const label = t.translateKey(opts.labelKey);
  const toggleId = `sb-modal-toggle-${opts.infoModalId}`;
  return `
    <li class="sb-card-listitem">
      <a class="sb-card-link" href="${escAttr(opts.href)}" download>
        <i class="sbi-download" aria-hidden="true"></i> <span data-i18n="${escAttr(opts.labelKey)}">${label}</span>
      </a>
      <button
        type="button"
        class="sb-btn sb-btn--surface sb-btn--icon"
        data-sb-modal-open="${escAttr(toggleId)}"
        data-i18n-aria-label="button.info"
        aria-label="${escAttr(t.translateKey("button.info"))}"
        title="${escAttr(t.translateKey("button.info"))}"
      ><i class="sbi-info" aria-hidden="true"></i></button>
    </li>
  `;
}

function resolveExtraPrintoutUrl(state: InitialState, file: string): string {
  const raw = (file ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("/")) return raw;

  const bookBaseDir = joinUrl(SONGBOOK_DATA_BASE_URL, state.books.book_path);

  // If the user gives a subpath, interpret it as relative to book_path.
  if (raw.includes("/")) return joinUrl(bookBaseDir, raw);

  // Otherwise, assume it lives under the default printouts directory.
  return joinUrl(joinUrl(bookBaseDir, "printouts"), raw);
}

function renderAllModals(state: InitialState, t: Translator, enabled: SongbookBook[]): string {
  const renderModal = (opts: {
    modalId: string;
    titleId: string;
    titleKey: string;
    bodyHtml: string;
    bodyI18nKey?: string;
  }): string => {
    const toggleId = `sb-modal-toggle-${opts.modalId}`;

    return `
      <div class="sb-modal-wrap">
        <input id="${escAttr(toggleId)}" class="sb-modal-toggle" type="checkbox" autocomplete="off" aria-hidden="true" />
        <div class="sb-modal" role="dialog" aria-modal="true" aria-labelledby="${escAttr(opts.titleId)}" aria-hidden="true" inert hidden>
          <label class="sb-modal-backdrop" for="${escAttr(toggleId)}" aria-hidden="true"></label>
          <div class="sb-modal-dialog sb-close-layout" role="document" tabindex="-1">
            <div class="sb-close-rail">
              <button
                type="button"
                class="sb-btn sb-btn--surface sb-btn--icon sb-modal-close"
                data-sb-modal-close="${escAttr(toggleId)}"
                data-i18n-aria-label="button.close"
                aria-label="${escAttr(t.translateKey("button.close"))}"
              ><i class="sbi-x" aria-hidden="true"></i></button>
            </div>
            <div class="sb-close-content">
              <header class="sb-modal-header">
                <h3 id="${escAttr(opts.titleId)}" data-i18n="${escAttr(opts.titleKey)}">${translatedText(t, opts.titleKey)}</h3>
              </header>
              <div class="sb-modal-body"${opts.bodyI18nKey ? richTranslationAttrs(t, opts.bodyI18nKey) : ""}>${opts.bodyHtml}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const shared = [
    renderModal({
      modalId: "sb-modal-printout-booklet",
      titleId: "sb-modal-printout-booklet-title",
      titleKey: "card.printout.booklet.title",
      bodyHtml: renderRichTranslationHtml(t, "card.printout.booklet.info", state.site),
      bodyI18nKey: "card.printout.booklet.info",
    }),
    renderModal({
      modalId: "sb-modal-printout-easy",
      titleId: "sb-modal-printout-easy-title",
      titleKey: "card.printout.easy.title",
      bodyHtml: renderRichTranslationHtml(t, "card.printout.easy.info", state.site),
      bodyI18nKey: "card.printout.easy.info",
    }),
  ].join("");

  const extraModals: string[] = [];
  enabled.forEach((book, idx0) => {
    const bookId = idx0 + 1;
    const extra = (book.extra_printout_links ?? []).filter(
      (x): x is SongbookAltVersionLink => !!x && !!x.enabled,
    );
    extra.forEach((link, idx1) => {
      const n = idx1 + 1;
      const titleKey = `DYNAMIC.book.${bookId}.extraprintout.title.${n}`;
      const infoKey = `DYNAMIC.book.${bookId}.extraprintout.info.${n}`;
      const modalId = `sb-modal-book-${bookId}-extraprintout-${n}`;

      const infoHtml = renderRichTranslationHtml(t, infoKey, state.site);
      const file = (link.file ?? "").trim();

      const bodyHtml = `
        ${file ? `<p><strong>File:</strong> <code>${escText(file)}</code></p>` : ""}
        ${infoHtml ? `<div${richTranslationAttrs(t, infoKey)}>${infoHtml}</div>` : `<div data-i18n="no-info">${translatedText(t, "no-info")}</div>`}
      `;

      extraModals.push(
        renderModal({
          modalId,
          titleId: `${modalId}-title`,
          titleKey,
          bodyHtml,
        }),
      );
    });
  });

  return `
    <section class="sb-modals">
      ${shared}
      ${extraModals.join("")}
    </section>`;
}

function renderTagIcons(
  state: InitialState,
  t: Translator,
  book: SongbookBook,
  mode: "cover" | "text",
): string {
  const tagIds = book.tag_ids ?? [];
  if (tagIds.length === 0) return "";

  const tagsById = new Map<string, SongbookTag>();
  for (const tag of state.books.tags ?? []) tagsById.set(tag.id, tag);

  const icons: string[] = [];

  for (const tagId of tagIds) {
    const tag = tagsById.get(tagId);
    if (!tag) continue;

    const iconKey = `DYNAMIC.tag.${tag.id}.iconfile`;
    const textKey = `DYNAMIC.tag.${tag.id}.text`;

    // Only show tags that have an icon defined.
    const iconUrl = t.translateKey(iconKey);
    if (!iconUrl) continue;

    icons.push(
      `<img class="${mode === "cover" ? "sb-tag-icon sb-tag-icon--cover" : "sb-tag-icon sb-tag-icon--text"}" src="${escAttr(iconUrl)}" data-i18n-alt="${escAttr(textKey)}" alt="${escAttr(t.translateKey(textKey))}" fetchpriority="high" width="512" height="512" />`,
    );
  }

  if (icons.length === 0) return "";

  if (mode === "cover") {
    return `<div class="sb-card-tagicons" aria-hidden="true">${icons.join("")}</div>`;
  }

  return `<span class="sb-side-tagicons" aria-hidden="true">${icons.join("")}</span>`;
}

function renderBook(
  state: InitialState,
  t: Translator,
  book: SongbookBook,
  id: number,
  viewId: string,
): string {
  const pdfUrl = resolvePdfUrl(state.books, book);
  const multikeyJsonUrl = resolveMultikeyJsonUrl(state.books, book);
  const menuExtras = renderCardBackInner(state, t, book, id).trim();
  const pageLayoutName = `pdf-page-layout-${id}`;
  const fitModeName = `pdf-fit-mode-${id}`;
  const renderingProfileName = `pdf-rendering-profile-${id}`;
  const screenWakeLockName = `pdf-screen-wake-lock-${id}`;
  const iconButton = (classes: string, icon: string, key: string, extra = "") =>
    `<button type="button" class="sb-btn sb-btn--icon ${classes}" data-i18n-title="${key}" data-i18n-aria-label="${key}" title="${escAttr(t.translateKey(key))}" aria-label="${escAttr(t.translateKey(key))}" ${extra}><i class="${icon}" aria-hidden="true"></i></button>`;
  const radio = (
    name: string,
    idValue: string,
    dataName: string,
    value: string,
    key: string,
    checked = false,
  ) =>
    `<input type="radio" name="${escAttr(name)}" id="${escAttr(idValue)}" data-${dataName}="${value}"${checked ? " checked" : ""} disabled aria-disabled="true"><label class="sb-btn sb-btn--surface" for="${escAttr(idValue)}" data-i18n="${key}">${translatedText(t, key)}</label>`;
  const attachmentMenuItem = `<button type="button" class="sb-btn sb-btn--list" role="menuitemradio" data-pdf-sidebar-view="attachments" aria-controls="pdf-attachments-panel-${id}" aria-checked="false" data-i18n="pdf.attachments-tab">${escText(t.translateKey("pdf.attachments-tab"))}</button>`;
  const attachmentPanel = `<section id="pdf-attachments-panel-${id}" class="pdf-sidebar-panel" role="region" data-pdf-sidebar-view="attachments" data-i18n-aria-label="pdf.attachments-tab" aria-label="${escAttr(t.translateKey("pdf.attachments-tab"))}" hidden inert><div class="pdf-attachments"></div></section>`;
  const layersMenuItem = `<button type="button" class="sb-btn sb-btn--list" role="menuitemradio" data-pdf-sidebar-view="layers" aria-controls="pdf-layers-panel-${id}" aria-checked="false" data-i18n="pdf.layers-tab">${escText(t.translateKey("pdf.layers-tab"))}</button>`;
  const layersPanel = `<section id="pdf-layers-panel-${id}" class="pdf-sidebar-panel" role="region" data-pdf-sidebar-view="layers" data-i18n-aria-label="pdf.layers-tab" aria-label="${escAttr(t.translateKey("pdf.layers-tab"))}" hidden inert><div class="pdf-layers"></div></section>`;
  const informationRow = (field: string, key: string) =>
    `<div class="sb-pdf-document-information-row" data-sb-pdf-information-field="${field}" hidden><dt data-i18n="${key}">${escText(t.translateKey(key))}</dt><dd dir="auto"></dd></div>`;
  const informationRows = [
    ["title", "pdf.document-title"],
    ["author", "pdf.document-author"],
    ["subject", "pdf.document-subject"],
    ["keywords", "pdf.document-keywords"],
    ["creationDate", "pdf.document-creation-date"],
    ["modificationDate", "pdf.document-modification-date"],
    ["creator", "pdf.document-creator"],
    ["producer", "pdf.document-producer"],
    ["pdfFormatVersion", "pdf.document-pdf-version"],
    ["pageCount", "pdf.document-page-count"],
    ["language", "pdf.document-language"],
    ["isLinearized", "pdf.document-linearized"],
  ]
    .map(([field, key]) => informationRow(field, key))
    .join("");
  const viewerUi = `<div class="pdf-custom-ui">
    <div class="pdf-controls"><div class="pdf-controls-toolbar" role="toolbar" data-i18n-aria-label="pdf.menu-title" aria-label="${escAttr(t.translateKey("pdf.menu-title"))}">
      <div class="pdf-controls-toolbar-start">${iconButton("sb-btn--accent-surface pdf-sidebar-toggle-btn", "sbi-list-numbered", "pdf.sidebar-title", `aria-expanded="false" aria-controls="pdf-sidebar-${id}"`)}${iconButton("sb-btn--accent-surface pdf-fit-main-btn", "sbi-fit", "pdf.fit-button-title", 'disabled aria-disabled="true" style="visibility: hidden"')}</div>
      <div class="pdf-page-nav">${iconButton("sb-btn--accent-surface pdf-prev-page-btn", "sbi-caret-large-up", "button.prev-page", "disabled")}<input id="pdf-page-number-input-${id}" class="pdf-page-number-input" value="1" inputmode="numeric" autocomplete="off" data-i18n-aria-label="pdf.current-page" aria-label="${escAttr(t.translateKey("pdf.current-page"))}" disabled><span aria-hidden="true">/</span><span class="pdf-page-count" aria-live="polite" aria-atomic="true">?</span>${iconButton("sb-btn--accent-surface pdf-next-page-btn", "sbi-caret-large-down", "button.next-page", "disabled")}</div>
      <div class="pdf-controls-toolbar-end">
        <div class="pdf-search">${iconButton("sb-btn--accent-surface pdf-search-toggle-btn", "sbi-search", "pdf.search-button-title", `aria-expanded="false" aria-controls="pdf-search-panel-${id}"`)}<div id="pdf-search-panel-${id}" class="pdf-search-panel" role="search" aria-hidden="true" inert hidden data-open="false"><div class="pdf-search-controls"><input id="pdf-search-input-${id}" type="search" class="pdf-search-input" data-i18n-placeholder="pdf.search-placeholder" data-i18n-aria-label="pdf.search-button-title" placeholder="${escAttr(t.translateKey("pdf.search-placeholder"))}" autocomplete="off" aria-label="${escAttr(t.translateKey("pdf.search-button-title"))}">${iconButton("sb-btn--surface pdf-search-previous-btn", "sbi-caret-left", "button.prev-result", "disabled")}${iconButton("sb-btn--surface pdf-search-next-btn", "sbi-caret-right", "button.next-result", "disabled")}<span class="pdf-search-count" role="status" aria-live="polite" aria-atomic="true"></span>${iconButton("sb-btn--surface pdf-search-close-btn", "sbi-x", "button.close")}</div><div class="pdf-search-extension" data-pdfjs-viewer-extension="search"><a class="pdf-search-advanced-link" data-view-nav data-sb-view-target="search" href="/?view=search"><i class="sbi-search" aria-hidden="true"></i><span data-i18n="pdf.search-advanced">${translatedText(t, "pdf.search-advanced")}</span></a></div></div></div>
        <div class="pdf-menu">${iconButton("sb-btn--accent-surface pdf-menu-toggle-btn", "sbi-menu-down", "pdf.menu-title", `aria-expanded="false" aria-controls="pdf-menu-panel-${id}"`)}<div id="pdf-menu-panel-${id}" class="pdf-menu-panel" role="dialog" data-i18n-aria-label="pdf.menu-title" aria-label="${escAttr(t.translateKey("pdf.menu-title"))}" aria-hidden="true" inert hidden data-open="false">
          <div class="sb-close-layout">
          <div class="sb-close-rail sb-close-rail--start">${iconButton("sb-btn--surface pdf-menu-close-btn", "sbi-x", "button.close")}</div>
          <div class="sb-close-content pdf-menu-content">
          <div class="pdf-menu-actions"><div class="pdf-menu-primary-actions"><a class="sb-btn sb-btn--surface pdf-download-btn" href="${escAttr(pdfUrl)}" download><i class="sbi-download" aria-hidden="true"></i><span data-i18n="pdf.download-original">${translatedText(t, "pdf.download-original")}</span></a><button type="button" class="sb-btn sb-btn--surface pdf-download-filled-document-btn" hidden disabled aria-disabled="true"><i class="sbi-download" aria-hidden="true"></i><span data-i18n="pdf.download-filled-document">${translatedText(t, "pdf.download-filled-document")}</span></button><button type="button" class="sb-btn sb-btn--surface pdf-print-btn" aria-haspopup="dialog" aria-controls="pdf-print-setup-${id}"><i class="sbi-print" aria-hidden="true"></i><span data-i18n="button.print">${translatedText(t, "button.print")}</span></button>
          <button type="button" class="sb-btn sb-btn--surface sb-pdf-save-selected-btn" hidden disabled aria-disabled="true"><i class="sbi-download" aria-hidden="true"></i><span data-i18n="pdf.save-selected-keys">${translatedText(t, "pdf.save-selected-keys")}</span></button>
          <button type="button" class="sb-btn sb-btn--surface sb-pdf-reset-transpositions-btn" hidden disabled aria-disabled="true"><i class="sbi-transpose-reset" aria-hidden="true"></i><span data-i18n="pdf.reset-transpositions">${translatedText(t, "pdf.reset-transpositions")}</span></button>
          </div><div class="pdf-menu-secondary-actions"><button type="button" class="sb-btn sb-btn--surface sb-pdf-document-information-btn" aria-haspopup="dialog" aria-controls="pdf-document-information-${id}" disabled aria-disabled="true"><i class="sbi-info" aria-hidden="true"></i><span data-i18n="pdf.document-information">${translatedText(t, "pdf.document-information")}</span></button><button type="button" class="sb-btn sb-btn--surface pdf-text-selection-toggle-btn" aria-pressed="false" disabled aria-disabled="true"><i class="sbi-text-selection" aria-hidden="true"></i><span data-i18n="pdf.text-selection">${translatedText(t, "pdf.text-selection")}</span></button></div></div>
          ${menuExtras ? `<div class="pdf-menu-extension" data-pdfjs-viewer-extension="menu"><div class="pdf-menu-extras">${menuExtras}</div></div>` : ""}
          <div class="pdf-navigation-mode-row"><fieldset class="pdf-navigation-history-group"><legend data-i18n="pdf.navigation-history">${translatedText(t, "pdf.navigation-history")}</legend><div class="pdf-navigation-history-controls">${iconButton("sb-btn--surface pdf-navigation-history-back-btn", "sbi-arrow-left", "pdf.navigation-history-back", 'disabled aria-disabled="true"')}${iconButton("sb-btn--surface pdf-navigation-history-forward-btn", "sbi-arrow-right", "pdf.navigation-history-forward", 'disabled aria-disabled="true"')}</div></fieldset><fieldset class="pdf-viewing-mode-group"><legend data-i18n="pdf.view-mode">${translatedText(t, "pdf.view-mode")}</legend><div class="pdf-viewing-mode-controls">${iconButton("sb-btn--surface pdf-fullscreen-toggle-btn", "sbi-fullscreen", "pdf.fullscreen", 'aria-pressed="false"')}${iconButton("sb-btn--surface pdf-presentation-toggle-btn", "sbi-presentation", "pdf.presentation", 'aria-pressed="false" disabled aria-disabled="true"')}</div></fieldset></div>
          <fieldset class="pdf-zoom-controls"><legend data-i18n="pdf.zoom-label">${translatedText(t, "pdf.zoom-label")}</legend><div class="pdf-zoom-row"><div class="pdf-zoom-slider-wrap"><input type="range" class="pdf-zoom-slider" min="0" max="100" value="0" data-i18n-aria-label="pdf.zoom-label" aria-label="${escAttr(t.translateKey("pdf.zoom-label"))}" disabled></div>${iconButton("sb-btn--surface pdf-fit-menu-btn", "sbi-fit", "pdf.fit-button-title", "disabled")}</div></fieldset>
          <fieldset><legend data-i18n="pdf.fit-mode">${translatedText(t, "pdf.fit-mode")}</legend><div class="sb-toggle-group pdf-fit-mode-group">${radio(fitModeName, `${fitModeName}-auto`, "pdf-fit-mode", "auto", "pdf.fit-mode-auto")}${radio(fitModeName, `${fitModeName}-contain`, "pdf-fit-mode", "contain", "pdf.fit-mode-contain")}${radio(fitModeName, `${fitModeName}-width`, "pdf-fit-mode", "width", "pdf.fit-mode-width")}${radio(fitModeName, `${fitModeName}-height`, "pdf-fit-mode", "height", "pdf.fit-mode-height")}</div></fieldset>
          <fieldset><legend data-i18n="pdf.page-layout">${translatedText(t, "pdf.page-layout")}</legend><div class="sb-toggle-group pdf-page-layout-group">${radio(pageLayoutName, `${pageLayoutName}-auto`, "pdf-page-layout", "auto", "pdf.page-layout-auto")}${radio(pageLayoutName, `${pageLayoutName}-single`, "pdf-page-layout", "single", "pdf.page-layout-single")}${radio(pageLayoutName, `${pageLayoutName}-book`, "pdf-page-layout", "book", "pdf.page-layout-book")}</div></fieldset>
          <fieldset><legend data-i18n="pdf.rotation">${translatedText(t, "pdf.rotation")}</legend><div class="pdf-rotation-controls">${iconButton("sb-btn--surface pdf-rotate-counterclockwise-btn", "sbi-arrow-clockwise", "pdf.rotate-counterclockwise", 'disabled aria-disabled="true"')}${iconButton("sb-btn--surface pdf-reset-rotation-btn", "sbi-reset-rotation", "pdf.reset-rotation", 'disabled aria-disabled="true"')}${iconButton("sb-btn--surface pdf-rotate-clockwise-btn", "sbi-arrow-clockwise", "pdf.rotate-clockwise", 'disabled aria-disabled="true"')}</div></fieldset>
          <fieldset><legend data-i18n="pdf.screen-wake-lock">${translatedText(t, "pdf.screen-wake-lock")}</legend><div class="sb-toggle-group pdf-screen-wake-lock-group">${radio(screenWakeLockName, `${screenWakeLockName}-never`, "pdf-screen-wake-lock", "never", "pdf.screen-wake-lock-never")}${radio(screenWakeLockName, `${screenWakeLockName}-always`, "pdf-screen-wake-lock", "always", "pdf.screen-wake-lock-always", true)}${radio(screenWakeLockName, `${screenWakeLockName}-fullscreen-only`, "pdf-screen-wake-lock", "fullscreen-only", "pdf.screen-wake-lock-fullscreen")}${radio(screenWakeLockName, `${screenWakeLockName}-presentation-only`, "pdf-screen-wake-lock", "presentation-only", "pdf.screen-wake-lock-presentation")}${radio(screenWakeLockName, `${screenWakeLockName}-presentation-or-fullscreen`, "pdf-screen-wake-lock", "presentation-or-fullscreen", "pdf.screen-wake-lock-either")}</div></fieldset>
          <fieldset><legend class="sb-rendering-heading" data-i18n="pdf.rendering-profile">${translatedText(t, "pdf.rendering-profile")}</legend><div class="sb-toggle-group sb-toggle-group-rendering pdf-rendering-profile-group">${radio(renderingProfileName, `${renderingProfileName}-conservative`, "pdf-rendering-profile", "conservative", "pdf.rendering-profile-conservative")}${radio(renderingProfileName, `${renderingProfileName}-balanced`, "pdf-rendering-profile", "balanced", "pdf.rendering-profile-balanced")}${radio(renderingProfileName, `${renderingProfileName}-aggressive`, "pdf-rendering-profile", "aggressive", "pdf.rendering-profile-aggressive")}</div></fieldset>
          </div></div>
        </div></div>
      </div>
    </div></div>
    <div class="pdf-presentation-controls" role="toolbar" data-i18n-aria-label="pdf.presentation-controls" aria-label="${escAttr(t.translateKey("pdf.presentation-controls"))}" hidden inert aria-hidden="true">${iconButton("sb-btn--surface pdf-presentation-previous-btn", "sbi-caret-large-up", "pdf.presentation-previous", 'disabled aria-disabled="true"')}${iconButton("sb-btn--surface pdf-presentation-exit-btn", "sbi-x", "pdf.exit-presentation")}${iconButton("sb-btn--surface pdf-presentation-next-btn", "sbi-caret-large-down", "pdf.presentation-next", 'disabled aria-disabled="true"')}</div>
    <div class="pdf-viewer-body">
      <progress class="pdf-document-progress" max="1" value="0" data-i18n-aria-label="accessibility.working" aria-label="${escAttr(t.translateKey("accessibility.working"))}"></progress>
      <aside id="pdf-sidebar-${id}" class="pdf-sidebar" tabindex="-1" data-open="false" data-i18n-aria-label="pdf.sidebar-title" aria-label="${escAttr(t.translateKey("pdf.sidebar-title"))}" aria-hidden="true" inert>
        <div class="pdf-sidebar-header"><div class="pdf-sidebar-primary-views" role="toolbar" data-i18n-aria-label="pdf.sidebar-title" aria-label="${escAttr(t.translateKey("pdf.sidebar-title"))}"><button type="button" class="sb-btn sb-btn--surface pdf-sidebar-view-button" data-pdf-sidebar-view="outline" aria-controls="pdf-outline-panel-${id}" aria-pressed="true" data-i18n="pdf.outline-tab">${escText(t.translateKey("pdf.outline-tab"))}</button><button type="button" class="sb-btn sb-btn--surface pdf-sidebar-view-button" data-pdf-sidebar-view="thumbnails" aria-controls="pdf-thumbnails-panel-${id}" aria-pressed="false" data-i18n="pdf.thumbnails-tab">${escText(t.translateKey("pdf.thumbnails-tab"))}</button></div><div class="pdf-sidebar-more">${iconButton("sb-btn--surface pdf-sidebar-more-toggle", "sbi-caret-down", "pdf.sidebar-more-views", `aria-haspopup="menu" aria-controls="pdf-sidebar-more-menu-${id}" aria-expanded="false"`)}<div id="pdf-sidebar-more-menu-${id}" class="pdf-sidebar-more-menu" role="menu" data-i18n-aria-label="pdf.sidebar-more-views" aria-label="${escAttr(t.translateKey("pdf.sidebar-more-views"))}" aria-hidden="true" hidden inert>${attachmentMenuItem}${layersMenuItem}</div></div>${iconButton("sb-btn--surface pdf-sidebar-close-btn", "sbi-x", "button.close")}</div>
        <section id="pdf-outline-panel-${id}" class="pdf-sidebar-panel" role="region" data-pdf-sidebar-view="outline" data-i18n-aria-label="pdf.outline-tab" aria-label="${escAttr(t.translateKey("pdf.outline-tab"))}"><div class="pdf-outline-filter"><input id="pdf-outline-filter-input-${id}" type="search" class="pdf-outline-filter-input" data-i18n-placeholder="pdf.filter-placeholder" data-i18n-aria-label="pdf.filter-placeholder" placeholder="${escAttr(t.translateKey("pdf.filter-placeholder"))}" autocomplete="off"></div><div class="pdf-outline"></div></section>
        <section id="pdf-thumbnails-panel-${id}" class="pdf-sidebar-panel" role="region" data-pdf-sidebar-view="thumbnails" data-i18n-aria-label="pdf.thumbnails-tab" aria-label="${escAttr(t.translateKey("pdf.thumbnails-tab"))}" hidden inert><div class="pdf-thumbnails"></div></section>
        ${attachmentPanel}
        ${layersPanel}
      </aside>
      <div class="pdf-container" tabindex="0"><noscript><p class="noscript"><span data-i18n="pdf.noscript">${escText(t.translateKey("pdf.noscript"))}</span><br><b><a href="${escAttr(pdfUrl)}">${escText(book.file)}</a></b></p></noscript></div>
    </div>
    <dialog id="pdf-document-information-${id}" class="sb-pdf-document-information" aria-labelledby="pdf-document-information-title-${id}">
      <div class="sb-pdf-document-information-dialog sb-close-layout">
        <div class="sb-close-rail">${iconButton("sb-btn--surface sb-pdf-document-information-close", "sbi-x", "button.close")}</div>
        <div class="sb-close-content">
        <header class="sb-pdf-document-information-header"><h2 id="pdf-document-information-title-${id}" data-i18n="pdf.document-information">${escText(t.translateKey("pdf.document-information"))}</h2></header>
        <p class="sb-pdf-document-information-status" role="status" aria-live="polite"></p>
        <dl class="sb-pdf-document-information-list">${informationRows}</dl>
        <div class="sb-pdf-document-information-actions"><button type="button" class="sb-btn sb-btn--surface sb-pdf-document-information-accept" data-i18n="button.ok">${escText(t.translateKey("button.ok"))}</button></div>
        </div>
      </div>
    </dialog>
    <dialog id="pdf-print-setup-${id}" class="pdf-print-setup sb-pdf-print-setup" aria-labelledby="pdf-print-title-${id}">
      <form method="dialog" autocomplete="off" class="sb-close-layout">
        <div class="sb-close-rail">${iconButton("sb-btn--surface pdf-print-close", "sbi-x", "button.close")}</div>
        <div class="sb-close-content">
        <header class="pdf-print-header"><h2 id="pdf-print-title-${id}" data-i18n="pdf.print-setup">${escText(t.translateKey("pdf.print-setup"))}</h2></header>
        <div class="pdf-print-basic">
          <div class="pdf-print-control-row"><div class="pdf-print-control"><label for="pdf-print-pages-${id}" data-i18n="pdf.print-pages">${escText(t.translateKey("pdf.print-pages"))}</label><select id="pdf-print-pages-${id}" class="pdf-print-pages"><option value="all" data-i18n="pdf.print-all">${escText(t.translateKey("pdf.print-all"))}</option><option value="custom" data-i18n="pdf.print-custom">${escText(t.translateKey("pdf.print-custom"))}</option></select></div><div class="pdf-print-conditional pdf-print-range-wrap" hidden inert aria-hidden="true"><label for="pdf-print-range-${id}" data-i18n="pdf.print-range">${escText(t.translateKey("pdf.print-range"))}</label><input id="pdf-print-range-${id}" class="pdf-print-range" data-i18n-placeholder="pdf.print-range-placeholder" placeholder="${escAttr(t.translateKey("pdf.print-range-placeholder"))}" autocomplete="off" enterkeyhint="done"></div></div>
        <div class="pdf-print-control-row"><div class="pdf-print-control"><label for="pdf-print-sheet-${id}" data-i18n="pdf.print-sheet">${escText(t.translateKey("pdf.print-sheet"))}</label><select id="pdf-print-sheet-${id}" class="pdf-print-sheet" aria-describedby="pdf-print-sheet-help-${id}"><option value="a3" data-i18n="pdf.print-sheet-a3">${escText(t.translateKey("pdf.print-sheet-a3"))}</option><option value="a4" data-i18n="pdf.print-sheet-a4" selected>${escText(t.translateKey("pdf.print-sheet-a4"))}</option><option value="a5" data-i18n="pdf.print-sheet-a5">${escText(t.translateKey("pdf.print-sheet-a5"))}</option><option value="letter" data-i18n="pdf.print-sheet-letter">${escText(t.translateKey("pdf.print-sheet-letter"))}</option><option value="legal" data-i18n="pdf.print-sheet-legal">${escText(t.translateKey("pdf.print-sheet-legal"))}</option><option value="custom" data-i18n="pdf.print-sheet-custom">${escText(t.translateKey("pdf.print-sheet-custom"))}</option></select><small id="pdf-print-sheet-help-${id}" class="pdf-print-help pdf-print-sheet-help" data-i18n="pdf.print-sheet-help">${escText(t.translateKey("pdf.print-sheet-help"))}</small></div><div class="pdf-print-conditional pdf-print-custom-sheet" hidden inert aria-hidden="true"><label for="pdf-print-sheet-width-${id}" data-i18n="pdf.print-sheet-width">${escText(t.translateKey("pdf.print-sheet-width"))}</label><input id="pdf-print-sheet-width-${id}" class="pdf-print-sheet-width" type="number" min="1" step="any" value="210" autocomplete="off"><label for="pdf-print-sheet-height-${id}" data-i18n="pdf.print-sheet-height">${escText(t.translateKey("pdf.print-sheet-height"))}</label><input id="pdf-print-sheet-height-${id}" class="pdf-print-sheet-height" type="number" min="1" step="any" value="297" autocomplete="off"><label for="pdf-print-sheet-unit-${id}" data-i18n="pdf.print-sheet-unit">${escText(t.translateKey("pdf.print-sheet-unit"))}</label><select id="pdf-print-sheet-unit-${id}" class="pdf-print-sheet-unit"><option value="mm">mm</option><option value="pt">pt</option><option value="in">in</option></select></div></div>
        </div>
        <details class="pdf-print-more sb-expandable-disclosure"><summary class="sb-expandable-header"><i class="sbi-triangle-right sb-expandable-triangle" aria-hidden="true"></i><span data-i18n="pdf.print-more">${escText(t.translateKey("pdf.print-more"))}</span></summary><div class="sb-expandable-content"><div class="sb-pdf-print-fields pdf-print-fields">
          <div class="pdf-print-control-row"><div class="pdf-print-control"><label for="pdf-print-layout-${id}" data-i18n="pdf.print-layout">${escText(t.translateKey("pdf.print-layout"))}</label><select id="pdf-print-layout-${id}" class="pdf-print-layout" aria-describedby="pdf-print-layout-help-${id}"><option value="auto" data-i18n="pdf.print-layout-auto">${escText(t.translateKey("pdf.print-layout-auto"))}</option><option value="single" data-i18n="pdf.print-layout-single">${escText(t.translateKey("pdf.print-layout-single"))}</option><option value="spread" data-i18n="pdf.print-layout-spread">${escText(t.translateKey("pdf.print-layout-spread"))}</option></select><small id="pdf-print-layout-help-${id}" class="pdf-print-help pdf-print-layout-help" data-i18n="pdf.print-layout-auto-help">${escText(t.translateKey("pdf.print-layout-auto-help"))}</small></div><div class="pdf-print-conditional pdf-print-side" hidden inert aria-hidden="true"><label for="pdf-print-side-${id}" data-i18n="pdf.print-first-side">${escText(t.translateKey("pdf.print-first-side"))}</label><select id="pdf-print-side-${id}" class="pdf-print-side-select"><option value="auto" data-i18n="pdf.print-auto">${escText(t.translateKey("pdf.print-auto"))}</option><option value="right" data-i18n="pdf.print-right">${escText(t.translateKey("pdf.print-right"))}</option><option value="left" data-i18n="pdf.print-left">${escText(t.translateKey("pdf.print-left"))}</option></select></div></div>
          <div class="pdf-print-control-row"><div class="pdf-print-control"><label for="pdf-print-orientation-${id}" data-i18n="pdf.print-orientation">${escText(t.translateKey("pdf.print-orientation"))}</label><select id="pdf-print-orientation-${id}" class="pdf-print-orientation" aria-describedby="pdf-print-orientation-help-${id}"><option value="auto" data-i18n="pdf.print-auto">${escText(t.translateKey("pdf.print-auto"))}</option><option value="portrait" data-i18n="pdf.print-portrait">${escText(t.translateKey("pdf.print-portrait"))}</option><option value="landscape" data-i18n="pdf.print-landscape">${escText(t.translateKey("pdf.print-landscape"))}</option></select><small id="pdf-print-orientation-help-${id}" class="pdf-print-help pdf-print-orientation-help" data-i18n="pdf.print-orientation-help">${escText(t.translateKey("pdf.print-orientation-help"))}</small></div></div>
          <div class="pdf-print-control-row"><div class="pdf-print-control"><label for="pdf-print-page-scaling-${id}" data-i18n="pdf.print-page-scaling">${escText(t.translateKey("pdf.print-page-scaling"))}</label><select id="pdf-print-page-scaling-${id}" class="pdf-print-page-scaling" aria-describedby="pdf-print-page-scaling-help-${id}"><option value="fit" data-i18n="pdf.print-page-scaling-fit">${escText(t.translateKey("pdf.print-page-scaling-fit"))}</option><option value="shrink-to-fit" data-i18n="pdf.print-page-scaling-shrink">${escText(t.translateKey("pdf.print-page-scaling-shrink"))}</option></select><small id="pdf-print-page-scaling-help-${id}" class="pdf-print-help pdf-print-page-scaling-help" data-i18n="pdf.print-page-scaling-help">${escText(t.translateKey("pdf.print-page-scaling-help"))}</small></div><div class="pdf-print-control"><label for="pdf-print-quality-${id}" data-i18n="pdf.print-quality">${escText(t.translateKey("pdf.print-quality"))}</label><select id="pdf-print-quality-${id}" class="pdf-print-quality" aria-describedby="pdf-print-quality-help-${id}"><option value="300" data-i18n="pdf.print-quality-300">${escText(t.translateKey("pdf.print-quality-300"))}</option><option value="600" data-i18n="pdf.print-quality-600">${escText(t.translateKey("pdf.print-quality-600"))}</option></select><small id="pdf-print-quality-help-${id}" class="pdf-print-help pdf-print-quality-help" data-i18n="pdf.print-resolution-help">${escText(t.translateKey("pdf.print-resolution-help"))}</small></div></div>
        </div></div></details>
        <div class="pdf-print-feedback">
          <p class="pdf-print-status" role="status" aria-live="polite"></p>
          <progress class="pdf-print-progress" max="1" value="0" data-i18n-aria-label="pdf.print-progress-label" aria-label="${escAttr(t.translateKey("pdf.print-progress-label"))}" hidden></progress>
          <p class="pdf-print-fallback-warning" aria-live="polite" hidden></p>
        </div>
        <p class="pdf-print-guidance"></p>
        <div class="pdf-print-actions"><button type="button" class="sb-btn sb-btn--surface pdf-print-cancel" data-i18n="button.cancel">${escText(t.translateKey("button.cancel"))}</button><button type="button" class="sb-btn sb-btn--surface pdf-print-source" hidden data-i18n="pdf.print-source">${escText(t.translateKey("pdf.print-source"))}</button><button type="submit" class="sb-btn sb-btn--surface pdf-print-submit"><span data-i18n="pdf.print-open">${escText(t.translateKey("pdf.print-open"))}</span><small class="pdf-print-submit-detail"></small></button></div>
        </div>
      </form>
    </dialog>
  </div>`;

  // Full-screen viewer; content rendered SSR + client initializes PdfjsViewer.
  return `
    <section class="sb-pdf" data-sb-view="${escAttr(viewId)}">
      <div class="pdf-viewer" data-pdfjs-viewer-root data-sb-pdf-url="${escAttr(pdfUrl)}"${multikeyJsonUrl ? ` data-sb-multikey-json-url="${escAttr(multikeyJsonUrl)}"` : ""}>
        ${viewerUi}
      </div>
    </section>
  `;
}
